import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentIcon,
  PlusIcon,
  ArrowPathIcon,
  NoSymbolIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { apiClient as sharedApiClient } from '../../services/api';
import { useOperationsAPI } from '../../context/useOperationsAPI';
import PageLayout from '../../components/PageLayout';
import { AdminModalShell } from '../../components/AdminSurface';
import { useAuth } from '../../context/AuthContext';

type Tool = { name: string; description: string; write: boolean };
type Credential = {
  id: string;
  name: string;
  prefix: string;
  company_ids: string[];
  tools: string[];
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
};
type Configuration = {
  endpoint: string;
  companies: { id: string; name: string }[];
  tools: Tool[];
  credentials: Credential[];
};
type Action = {
  id: string;
  tool: string;
  status: string;
  company_id: string;
  preview: Record<string, any>;
  result: any;
  error: string | null;
  created_at: string;
  expires_at: string;
  approved_at?: string | null;
  fingerprint?: string;
};
type Decision = {
  id: string;
  approve: boolean;
  phase: 'sending' | 'checking' | 'unknown';
};
const actionStatus = (action: Action) =>
  action.status === 'needs_review'
    ? action.result?.status === 'completed_with_issues'
      ? 'Completed with issues'
      : 'Requires reconciliation'
    : action.status === 'succeeded'
    ? 'Completed'
    : action.status === 'approved'
    ? 'Approved - queued for execution'
    : label(action.status);
const label = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) +
  (['unit_amount', 'discount_amount'].includes(value) ? ' (cents)' : '');
const toggle = (values: string[], value: string) =>
  values.includes(value)
    ? values.filter((x) => x !== value)
    : [...values, value];
const date = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : 'Never';

function RecordView({
  value,
  compact = false,
}: {
  value: any;
  compact?: boolean;
}) {
  if (value === null || value === undefined)
    return <span className="text-on-surface-variant">None</span>;
  if (Array.isArray(value))
    return (
      <ol className="divide-y divide-outline/40">
        {value.map((v, i) => (
          <li className="py-2" key={i}>
            <RecordView value={v} compact={compact} />
          </li>
        ))}
      </ol>
    );
  if (typeof value === 'object')
    return (
      <dl className="space-y-2">
        {Object.entries(value).map(([key, v]) => (
          <div
            key={key}
            className={`grid min-w-0 gap-1 ${
              compact ? '' : 'sm:grid-cols-[160px_minmax(0,1fr)]'
            }`}
          >
            <dt className="break-words text-sm font-medium text-on-surface-variant">
              {label(key)}
            </dt>
            <dd className="min-w-0 break-words text-sm">
              <RecordView value={v} compact />
            </dd>
          </div>
        ))}
      </dl>
    );
  return (
    <span className="whitespace-pre-wrap break-words">
      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
    </span>
  );
}

export function InvoiceLinkReview({
  rows,
  result,
}: {
  rows: any[];
  result?: any;
}) {
  const [search, setSearch] = useState('');
  const visible = rows.filter((row) =>
    JSON.stringify(row).toLowerCase().includes(search.toLowerCase())
  );
  return (
    <section
      className="min-w-0 space-y-3"
      aria-label="Invoice reservation corrections"
    >
      <input
        aria-label="Search invoice corrections"
        className="w-full rounded-md border border-outline bg-surface p-2"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search invoice or reservation"
      />
      <div className="max-w-full overflow-x-auto">
        <table className="w-full table-fixed text-left text-sm">
          <thead className="hidden md:table-header-group">
            <tr className="border-b border-outline/40">
              {[
                'Invoice',
                'Previous reservation',
                'New reservation',
                'Decision / result',
              ].map((name) => (
                <th key={name} scope="col" className="p-2">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const outcome = result?.rows?.find(
                (item: any) => item.invoice_id === row.invoice_id
              );
              const { fingerprint: _fingerprint, ...details } = row;
              return (
                <tr
                  key={row.invoice_id}
                  className="block border-b border-outline/40 align-top md:table-row"
                >
                  <td className="block break-words p-2 md:table-cell">
                    <span className="block font-semibold md:hidden">
                      Invoice
                    </span>
                    {row.invoice_number || row.invoice_id}
                    <details className="mt-2">
                      <summary className="cursor-pointer">Details</summary>
                      <RecordView value={details} compact />
                    </details>
                  </td>
                  <td className="block break-words p-2 md:table-cell">
                    <span className="block font-semibold md:hidden">
                      Previous reservation
                    </span>
                    {row.previous_reservation_id || 'None'}
                  </td>
                  <td className="block break-words p-2 md:table-cell">
                    <span className="block font-semibold md:hidden">
                      New reservation
                    </span>
                    {row.reservation_ref || row.reservation_id || 'None'}
                  </td>
                  <td className="block break-words p-2 md:table-cell">
                    <span className="block font-semibold md:hidden">
                      Decision / result
                    </span>
                    {label(row.decision)}
                    <p className="mt-1">{row.reason}</p>
                    {outcome && (
                      <p className="mt-2 font-semibold">
                        {label(outcome.status)}
                      </p>
                    )}
                    {outcome?.error && (
                      <p className="mt-1 text-red-600">{outcome.error}</p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-on-surface-variant">
        {visible.length} of {rows.length} corrections
      </p>
    </section>
  );
}

export function IntakeReview({ batch, result }: { batch: any; result?: any }) {
  const [search, setSearch] = useState('');
  const [decision, setDecision] = useState('all');
  const rows = (batch.rows || []).filter(
    (r: any) =>
      (decision === 'all' || r.decision === decision) &&
      `${r.key} ${r.operation} ${r.error || ''}`
        .toLowerCase()
        .includes(search.toLowerCase())
  );
  return (
    <div className="min-w-0 space-y-4">
      <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        {Object.entries(batch.counts || {}).map(([key, value]) => (
          <div key={key}>
            <dt>{label(key)}</dt>
            <dd className="font-semibold">{String(value)}</dd>
          </div>
        ))}
        <div>
          <dt>Draft invoice value</dt>
          <dd className="font-semibold">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format((batch.invoice_total_cents || 0) / 100)}
          </dd>
        </div>
      </dl>
      {batch.blocked && (
        <p role="alert" className="text-sm text-red-600">
          Conflicts must be resolved before approval.
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <input
          aria-label="Search batch rows"
          placeholder="Search source keys"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-0 flex-1 rounded-md border border-outline bg-surface p-2"
        />
        <select
          aria-label="Row decision"
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          className="rounded-md border border-outline bg-surface p-2"
        >
          {['all', 'create', 'match', 'resume', 'completed', 'conflict'].map(
            (v) => (
              <option key={v} value={v}>
                {label(v)}
              </option>
            )
          )}
        </select>
      </div>
      <div className="max-h-[55vh] overflow-auto border-y border-outline/40">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead className="sticky top-0 bg-surface">
            <tr>
              {['Source key', 'Operation', 'Decision', 'Result', 'Review'].map(
                (h) => (
                  <th key={h} className="p-3">
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any) => {
              const outcome = result?.rows?.find((r: any) => r.key === row.key);
              return (
                <tr
                  key={row.key}
                  className="border-t border-outline/40 align-top"
                >
                  <td className="max-w-[180px] break-words p-3">{row.key}</td>
                  <td className="p-3">{label(row.operation)}</td>
                  <td className="p-3">{label(row.decision)}</td>
                  <td className="p-3">
                    {outcome ? label(outcome.status) : 'Not run'}
                  </td>
                  <td className="max-w-[480px] p-3">
                    <details>
                      <summary className="cursor-pointer">Details</summary>
                      {(row.error || outcome?.error) && (
                        <p className="my-2 text-red-600">
                          {row.error || outcome.error}
                        </p>
                      )}
                      <RecordView
                        compact
                        value={{
                          requested: row.input,
                          current: row.current,
                          references_and_terms: row.references,
                          result: outcome || null,
                        }}
                      />
                    </details>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-on-surface-variant">
        {rows.length} of {batch.rows?.length || 0} rows
      </p>
    </div>
  );
}

type BulkOutcome =
  | 'checking'
  | 'ready'
  | 'submitting'
  | 'approved'
  | 'denied'
  | 'conflicted'
  | 'expired'
  | 'access_denied'
  | 'uncertain';
type BulkRow = {
  id: string;
  action?: Action;
  outcome: BulkOutcome;
  reviewed: boolean;
  fingerprint?: string;
  inspected?: boolean;
  error?: string;
};
type ReviewJournal = {
  version: 1;
  ids: string[];
  approve: boolean;
  outcomes?: Record<string, BulkOutcome>;
};
const journalKey = (ownerId: string) => `ai-action-review:v1:${ownerId}`;
const readJournal = (ownerId: string): ReviewJournal | null => {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(journalKey(ownerId)) || 'null'
    );
    if (
      value?.version === 1 &&
      typeof value.approve === 'boolean' &&
      Array.isArray(value.ids) &&
      value.ids.length > 0 &&
      value.ids.length <= 25 &&
      new Set(value.ids).size === value.ids.length &&
      value.ids.every(
        (id: unknown) =>
          typeof id === 'string' && /^[A-Za-z0-9-]{1,100}$/.test(id)
      )
    )
      return value;
  } catch {
    /* Recovery remains available in memory when storage is unavailable. */
  }
  return null;
};
async function limitedDecisions<T>(
  items: T[],
  work: (item: T) => Promise<void>
) {
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(3, items.length) }, async () => {
      while (next < items.length) await work(items[next++]);
    })
  );
}
const knownDecision = (
  action: Action,
  approve: boolean
): BulkOutcome | null => {
  if (
    approve &&
    action.approved_at &&
    ['approved', 'running', 'succeeded', 'needs_review', 'expired'].includes(
      action.status
    )
  )
    return 'approved';
  if (!approve && action.status === 'cancelled') return 'denied';
  if (!approve && action.status === 'approved') return null;
  if (
    action.status === 'expired' ||
    (action.status === 'pending_review' &&
      new Date(action.expires_at) <= new Date())
  )
    return 'expired';
  if (action.status !== 'pending_review') return 'conflicted';
  return null;
};
const rejectedDecision = (error: any): BulkOutcome => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return 'access_denied';
  if (status === 409 && /expir/i.test(String(error?.response?.data?.message)))
    return 'expired';
  return status >= 400 && status < 500 && ![408, 429].includes(status)
    ? 'conflicted'
    : 'uncertain';
};
// One read budget per mounted actor/session/business. No automatic decision retries.
export function createActionReadback(apiClient: typeof sharedApiClient) {
  let blockedUntil = 0;
  let active = true;
  let generation = 0;
  const pending = new Map<string, Promise<Action>>();
  const unavailable = () => ({ response: { status: 429, headers: {
    'retry-after': Math.max(1, Math.ceil((blockedUntil - Date.now()) / 1000)),
  }, data: { message: 'Readback is cooling down. Check again after the retry window.' } } });
  const observe = (error: any) => {
    if (!active || error?.response?.status !== 429) return;
    const headers = error.response.headers;
    const value = headers?.get?.('retry-after') ?? headers?.['retry-after'] ?? headers?.['Retry-After'];
    const seconds = typeof value === 'number' || (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value.trim()))
      ? Number(value) : NaN;
    const date = typeof value === 'string' ? Date.parse(value) : NaN;
    const delay = Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000
      : Number.isFinite(date) ? Math.max(0, date - Date.now()) : 60000;
    blockedUntil = Math.max(blockedUntil, Date.now() + Math.max(1000, delay));
  };
  return {
    observe,
    canRead: () => active && Date.now() >= blockedUntil,
    retryAt: () => blockedUntil,
    activate: () => { active = true; },
    dispose: () => { active = false; generation++; pending.clear(); },
    async read(id: string): Promise<Action> {
      if (!active) throw new Error('Review context changed');
      if (Date.now() < blockedUntil) throw unavailable();
      const existing = pending.get(id);
      if (existing) return existing;
      const startedGeneration = generation;
      const work = (async () => {
        try {
          const response = await apiClient.get(`/ai-access/actions/${encodeURIComponent(id)}`);
          if (!active || generation !== startedGeneration) throw new Error('Review context changed');
          if (response.data?.id !== id) throw new Error('Canonical action ID did not match');
          return response.data as Action;
        } catch (error) { if (generation === startedGeneration) observe(error); throw error; }
      })();
      pending.set(id, work);
      const clear = () => { if (pending.get(id) === work) pending.delete(id); };
      void work.then(clear, clear);
      return work;
    },
  };
}
type ActionReadback = ReturnType<typeof createActionReadback>;
const sessionGenerations = new WeakMap<object, number>();
let nextSessionGeneration = 0;
const sessionGeneration = (user: object | null) => {
  if (!user) return 0;
  if (!sessionGenerations.has(user)) sessionGenerations.set(user, ++nextSessionGeneration);
  return sessionGenerations.get(user);
};
const needsReadback = (row: BulkRow) => !['conflicted', 'expired', 'access_denied', 'denied'].includes(row.outcome)
  && !(row.outcome === 'approved' && row.action && ['succeeded', 'needs_review', 'expired'].includes(row.action.status));

export function ActionActivity({
  actions,
  ownerId,
  onReview,
  reviewScope = ownerId,
  readback,
}: {
  actions: Action[];
  ownerId: string;
  onReview: (id: string) => void;
  reviewScope?: string;
  readback?: ActionReadback;
}) {
  return <ScopedActionActivity key={reviewScope} actions={actions} ownerId={reviewScope} onReview={onReview} readback={readback} />;
}
function ScopedActionActivity({ actions, ownerId, onReview, readback }: {
  actions: Action[]; ownerId: string; onReview: (id: string) => void; readback?: ActionReadback;
}) {
  const client = useQueryClient();
  const apiClient = useOperationsAPI(sharedApiClient);
  const ownedReadback = useMemo(() => createActionReadback(apiClient), [apiClient]);
  const reads = readback || ownedReadback;
  useEffect(() => { ownedReadback.activate(); return () => ownedReadback.dispose(); }, [ownedReadback]);
  const exactAction = useCallback((id: string) => reads.read(id), [reads]);
  const [journal] = useState(() => readJournal(ownerId));
  const [selection, setSelection] = useState<Action[]>([]);
  const [rows, setRows] = useState<BulkRow[]>(
    () =>
      journal?.ids.map((id) => ({
        id,
        outcome: [
          'approved',
          'denied',
          'conflicted',
          'expired',
          'access_denied',
        ].includes(journal.outcomes?.[id] || '')
          ? journal.outcomes![id]
          : 'uncertain',
        reviewed: false,
      })) || []
  );
  const [direction, setDirection] = useState<boolean | null>(
    journal?.approve ?? null
  );
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [storageError, setStorageError] = useState('');
  const locked = useRef(false);
  const mounted = useRef(true);
  const currentRows = useRef(rows);
  const currentDirection = useRef(direction);
  currentRows.current = rows;
  currentDirection.current = direction;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const persist = useCallback(
    (items: BulkRow[], approve: boolean) => {
      try {
        sessionStorage.setItem(
          journalKey(ownerId),
          JSON.stringify({
            version: 1,
            ids: items.map((row) => row.id),
            approve,
            outcomes: Object.fromEntries(
              items.map((row) => [row.id, row.outcome])
            ),
          })
        );
        setStorageError('');
      } catch {
        setStorageError(
          'Recovery storage is unavailable. Keep this review open until every outcome is confirmed.'
        );
      }
    },
    [ownerId]
  );
  const update = useCallback(
    (row: BulkRow) => {
      if (!mounted.current) return;
      const next = currentRows.current.map((item) =>
        item.id === row.id ? row : item
      );
      currentRows.current = next;
      setRows(next);
      if (currentDirection.current !== null)
        persist(next, currentDirection.current);
    },
    [persist]
  );
  const check = useCallback(
    async (targets: BulkRow[], approve: boolean | null) => {
      if (!targets.length || locked.current || !reads.canRead()) return;
      locked.current = true;
      setRunning(true);
      try {
        await limitedDecisions(targets, async (row) => {
          if (!mounted.current) return;
          try {
            const action = await exactAction(row.id);
            const outcome =
              approve === null
                ? knownDecision(action, true) === null
                  ? 'ready'
                  : 'conflicted'
                : knownDecision(action, approve) ||
                  (['checking', 'submitting'].includes(row.outcome)
                    ? 'uncertain'
                    : row.outcome);
            update({
              ...row,
              action,
              outcome:
                action.status === 'expired' ||
                (action.status === 'pending_review' &&
                  new Date(action.expires_at) <= new Date())
                  ? action.approved_at && approve === true
                    ? 'approved'
                    : 'expired'
                  : outcome,
              reviewed: Boolean(
                row.reviewed && row.fingerprint === action.fingerprint
              ),
              inspected: Boolean(
                row.inspected && row.fingerprint === action.fingerprint
              ),
              fingerprint: action.fingerprint,
              error: ['approved', 'denied', 'ready', 'uncertain'].includes(outcome)
                ? undefined
                : row.error,
            });
          } catch (error) {
            update({
              ...row,
              action: undefined,
              reviewed: false,
              outcome:
                approve === null
                  ? rejectedDecision(error)
                  : row.outcome === 'checking'
                  ? 'uncertain'
                  : row.outcome,
              error:
                'Exact outcome unavailable. Access may have changed; check this ID again.',
            });
          }
        });
      } finally {
        locked.current = false;
        if (mounted.current) setRunning(false);
      }
    },
    [update, exactAction, reads]
  );
  useEffect(() => {
    if (!open || direction === null || running) return;
    const timer = setInterval(
      () => void check(currentRows.current.filter(needsReadback), direction),
      5000
    );
    return () => clearInterval(timer);
  }, [open, direction, running, check]);
  const reviewSelection = () => {
    const targets: BulkRow[] = selection.map((action) => ({
      id: action.id,
      outcome: 'checking',
      reviewed: false,
    }));
    currentRows.current = targets;
    setRows(targets);
    setOpen(true);
    void check(targets, null);
  };
  const submit = async (approve: boolean) => {
    if (locked.current || !reads.canRead()) return;
    const targets = currentRows.current.filter(
      (row) =>
        row.reviewed &&
        row.action &&
        (direction === null
          ? row.outcome === 'ready'
          : row.outcome === 'uncertain')
    );
    if (
      !targets.length ||
      targets.length > 25 ||
      (approve && targets.some((row) => row.action?.preview?.batch?.blocked)) ||
      new Set(targets.map((row) => row.action!.company_id)).size !== 1
    )
      return;
    // Persist identifiers before any decision. Recovery always reads receipts;
    // it requires a fresh acknowledgement for any decision still unconfirmed.
    currentDirection.current = approve;
    persist(currentRows.current, approve);
    locked.current = true;
    setRunning(true);
    setDirection(approve);
    try {
      await limitedDecisions(targets, async (row) => {
        if (!mounted.current) return;
        let action: Action;
        try {
          action = await exactAction(row.id);
        } catch (error) {
          update({
            ...row,
            action: undefined,
            reviewed: false,
            outcome:
              row.outcome === 'uncertain'
                ? 'uncertain'
                : rejectedDecision(error),
            error: 'Readback unavailable; no new decision was submitted.',
          });
          return;
        }
        const known = knownDecision(action, approve);
        if (known) {
          update({ ...row, action, outcome: known, error: undefined });
          return;
        }
        if (
          !action.fingerprint ||
          action.fingerprint !== row.fingerprint ||
          action.company_id !== row.action!.company_id
        ) {
          update({
            ...row,
            action,
            outcome: 'conflicted',
            reviewed: false,
            error: 'Reviewed preview changed. Review this action again.',
          });
          return;
        }
        if (!mounted.current) return;
        if (!reads.canRead()) {
          update({ ...row, action, outcome: 'uncertain', reviewed: false,
            error: 'Rate limit reached. Check the original ID after the retry window; no new decision was submitted.' });
          return;
        }
        update({ ...row, action, outcome: 'submitting', error: undefined });
        let outcome: BulkOutcome = 'uncertain';
        let message: string | undefined;
        try {
          const response = await apiClient.post(
            `/ai-access/actions/${encodeURIComponent(row.id)}/${
              approve ? 'approve' : 'cancel'
            }`,
            undefined,
            { timeout: 30000 }
          );
          if (response.data?.id !== row.id || response.data?.company_id !== action.company_id)
            throw new Error('Decision receipt did not match the reviewed action');
          action = response.data;
          outcome = knownDecision(action, approve) || 'uncertain';
        } catch (error: any) {
          reads.observe(error);
          outcome = rejectedDecision(error);
          message =
            error?.response?.data?.message ||
            'Decision response unavailable; checking the original ID.';
        }
        try {
          action = await exactAction(row.id);
          outcome = knownDecision(action, approve) || outcome;
          if (mounted.current)
            client.setQueryData(['ai-action', ownerId, row.id], action);
          update({
            ...row,
            action,
            outcome,
            error:
              outcome === 'approved' || outcome === 'denied'
                ? undefined
                : message,
          });
        } catch {
          update({
            ...row,
            action: undefined,
            reviewed: false,
            outcome,
            error:
              'Exact outcome unavailable. No business completion is confirmed.',
          });
        }
      });
    } finally {
      locked.current = false;
      if (mounted.current) {
        setRunning(false);
        void client.invalidateQueries('ai-actions');
      }
    }
  };
  const finish = () => {
    try {
      sessionStorage.removeItem(journalKey(ownerId));
    } catch {
      /* Keep in-memory controls usable. */
    }
    setRows([]);
    setSelection([]);
    setDirection(null);
    setOpen(false);
  };
  const ready = rows.filter((row) => row.outcome === 'ready');
  const uncertain = rows.filter((row) => row.outcome === 'uncertain');
  const canSubmit = ready.length > 0 && ready.every((row) => row.reviewed);
  const canResume = uncertain.some(
    (row) =>
      row.reviewed &&
      row.action &&
      (row.action.status === 'pending_review' ||
        (direction === false && row.action.status === 'approved'))
  );
  const button =
    'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-outline px-3 py-2 text-sm font-medium disabled:opacity-40';
  const tenants = Array.from(
    new Set(actions.map((action) => action.company_id))
  );
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {direction !== null ? (
          <button
            className={button}
            onClick={() => {
              setOpen(true);
              void check(currentRows.current, direction);
            }}
          >
            Resume previous review ({rows.length})
          </button>
        ) : (
          <>
            <span className="text-sm">{selection.length} / 25 selected</span>
            <button
              className={button}
              disabled={!selection.length || running}
              onClick={reviewSelection}
            >
              Review selected
            </button>
            <button
              className={button}
              disabled={!selection.length || running}
              onClick={() => setSelection([])}
            >
              Clear selection
            </button>
          </>
        )}
      </div>
      <table className="w-full min-w-[620px] text-left text-sm">
        <thead>
          <tr className="border-b border-outline/50">
            {['', 'Operation', 'Tenant', 'Requested', 'Status', ''].map(
              (heading, i) => (
                <th key={i} className="px-3 py-3">
                  {heading}
                </th>
              )
            )}
          </tr>
        </thead>
        {tenants.map((tenant) => (
          <tbody key={tenant}>
            <tr>
              <th
                colSpan={6}
                scope="rowgroup"
                className="bg-surface-variant px-3 py-2 text-left"
              >
                {actions.find((action) => action.company_id === tenant)?.preview
                  ?.tenant?.name || tenant}
              </th>
            </tr>
            {actions
              .filter((action) => action.company_id === tenant)
              .map((action) => {
                const checked = selection.some((item) => item.id === action.id);
                return (
                  <tr className="border-b border-outline/30" key={action.id}>
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        aria-label={`Select action ${action.id}`}
                        checked={checked}
                        disabled={
                          running ||
                          direction !== null ||
                          action.status !== 'pending_review' ||
                          (!checked &&
                            (selection.length >= 25 ||
                              (selection.length > 0 &&
                                selection[0].company_id !== tenant)))
                        }
                        onChange={() =>
                          setSelection((current) =>
                            checked
                              ? current.filter((item) => item.id !== action.id)
                              : current.length < 25 &&
                                (!current.length ||
                                  current[0].company_id === tenant)
                              ? [...current, action]
                              : current
                          )
                        }
                      />
                    </td>
                    <td className="px-3 py-4 font-medium">
                      {label(action.tool)}
                    </td>
                    <td className="px-3 py-4">
                      {action.preview?.tenant?.name}
                    </td>
                    <td className="px-3 py-4">{date(action.created_at)}</td>
                    <td className="px-3 py-4">{actionStatus(action)}</td>
                    <td className="px-3 py-4">
                      <button
                        className={button}
                        onClick={() => onReview(action.id)}
                      >
                        {action.status === 'pending_review'
                          ? 'Review'
                          : 'Details'}
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        ))}
        {!actions.length && (
          <tbody>
            <tr>
              <td
                colSpan={6}
                className="py-10 text-center text-on-surface-variant"
              >
                No activity
              </td>
            </tr>
          </tbody>
        )}
      </table>
      {open && (
        <AdminModalShell
          title="Review selected actions"
          maxWidthClassName="max-w-4xl"
          onClose={() => !running && setOpen(false)}
          footer={
            <div className="flex flex-wrap justify-end gap-3">
              <button
                className={button}
                disabled={running}
                onClick={() => void check(currentRows.current, direction)}
              >
                <ArrowPathIcon className="h-5 w-5" />
                Check outcomes
              </button>
              {direction === null ? (
                <>
                  <button
                    className={button}
                    disabled={running || !canSubmit}
                    onClick={() => void submit(false)}
                  >
                    <NoSymbolIcon className="h-5 w-5" />
                    Deny selected
                  </button>
                  <button
                    className={`${button} bg-primary text-on-primary`}
                    disabled={
                      running ||
                      !canSubmit ||
                      ready.some((row) => row.action?.preview?.batch?.blocked)
                    }
                    onClick={() => void submit(true)}
                  >
                    <CheckIcon className="h-5 w-5" />
                    Approve selected
                  </button>
                </>
              ) : (
                <button
                  className={button}
                  disabled={running || !canResume}
                  onClick={() => void submit(direction)}
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  Resume uncertain {direction ? 'approvals' : 'cancellations'}
                </button>
              )}
              <button
                className={button}
                disabled={
                  running || (direction !== null && uncertain.length > 0)
                }
                onClick={finish}
              >
                Finish review
              </button>
            </div>
          }
        >
          <p className="mb-4 text-sm">
            {rows.length} selected actions
            {rows[0]?.action
              ? ` for ${
                  rows[0].action.preview?.tenant?.name ||
                  rows[0].action.company_id
                }`
              : ''}
          </p>
          {!reads.canRead() && (
            <p role="status" className="mb-4 text-sm">
              Rate limit reached. Readback pauses until {new Date(reads.retryAt()).toLocaleTimeString()}.
              Decisions will not be retried automatically.
            </p>
          )}
          {storageError && (
            <p role="alert" className="mb-4 text-amber-700">
              {storageError}
            </p>
          )}
          <ol className="divide-y divide-outline/40">
            {rows.map((row) => (
              <li key={row.id} data-action-id={row.id} className="min-w-0 py-4">
                <p className="break-all font-semibold">
                  {row.action ? label(row.action.tool) : 'Action'}: {row.id}
                </p>
                <dl className="my-2 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <div>
                    <dt>Decision</dt>
                    <dd aria-live="polite">{label(row.outcome)}</dd>
                  </div>
                  <div>
                    <dt>Execution</dt>
                    <dd>
                      {row.action
                        ? actionStatus(row.action)
                        : 'Outcome unavailable'}
                    </dd>
                  </div>
                  {row.action && (
                    <div>
                      <dt>Tenant</dt>
                      <dd>
                        {row.action.preview?.tenant?.name ||
                          row.action.company_id}
                      </dd>
                    </div>
                  )}
                </dl>
                {row.error && (
                  <p role="alert" className="my-2 text-sm text-red-600">
                    {row.error}
                  </p>
                )}
                {row.action && (
                  <details
                    className="my-3"
                    onToggle={(event) => {
                      if (event.currentTarget.open && !row.inspected)
                        update({ ...row, inspected: true });
                    }}
                  >
                    <summary className="cursor-pointer">
                      Requested changes
                    </summary>
                    <RecordView value={row.action.preview} compact />
                    {row.action.result && (
                      <>
                        <h4 className="mt-3 font-semibold">
                          Recorded execution result
                        </h4>
                        <RecordView value={row.action.result} compact />
                      </>
                    )}
                    {row.action.error && (
                      <p role="alert" className="text-sm text-red-600">
                        {row.action.error}
                      </p>
                    )}
                  </details>
                )}
                {['ready', 'uncertain'].includes(row.outcome) &&
                  row.action &&
                  (row.action.status === 'pending_review' ||
                    (direction === false &&
                      row.action.status === 'approved')) && (
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        aria-label={`Reviewed action ${row.id}`}
                        disabled={running || !row.inspected || !row.fingerprint}
                        checked={row.reviewed}
                        onChange={(event) =>
                          update({ ...row, reviewed: event.target.checked })
                        }
                      />
                      I reviewed these changes for{' '}
                      {row.action.preview?.tenant?.name ||
                        row.action.company_id}
                      {row.action.preview?.customer_email_will_be_sent
                        ? ', including the customer email.'
                        : '.'}
                    </label>
                  )}
              </li>
            ))}
          </ol>
        </AdminModalShell>
      )}
    </>
  );
}

export default function AiAccess() {
  const { user, operationsScope, operationsCompanyId } = useAuth();
  // The app QueryClient survives tenant logouts. Never carry cached review
  // records or an acknowledged dialog into a different actor's session.
  const reviewScope = JSON.stringify([
    window.location.host,
    user?.id,
    user?.company_id,
    user?.operations_grant_version,
    user?.auth_identity_id,
    sessionGeneration(user),
    operationsScope,
    operationsCompanyId,
  ]);
  return user ? (
    <AiAccessWorkspace key={reviewScope} reviewScope={reviewScope} />
  ) : (
    <PageLayout>
      <p role="alert">Sign in to review your AI activity.</p>
    </PageLayout>
  );
}

function AiAccessWorkspace({ reviewScope }: { reviewScope: string }) {
  const { user, operationsScope, operationsCompanyId } = useAuth();
  const apiClient = useOperationsAPI(sharedApiClient);
  const client = useQueryClient();
  const readback = useMemo(() => createActionReadback(apiClient), [apiClient]);
  useEffect(() => { readback.activate(); return () => readback.dispose(); }, [readback]);
  const config = useQuery<Configuration>(
    ['ai-access', reviewScope],
    async () => (await apiClient.get('/ai-access')).data,
    { retry: false, cacheTime: 0 }
  );
  const history = useQuery<Action[]>(
    ['ai-actions', reviewScope],
    async () => (await apiClient.get('/ai-access/actions')).data,
    { enabled: Boolean(config.data), refetchInterval: 5000, cacheTime: 0 }
  );
  const [tab, setTab] = useState<'connections' | 'actions'>('connections');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [tenants, setTenants] = useState<string[]>([]);
  const [tools, setTools] = useState<string[]>([]);
  const [token, setToken] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [revoke, setRevoke] = useState<Credential | null>(null);
  const [busy, setBusy] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [decisionError, setDecisionError] = useState('');
  const detail = useQuery<Action>(
    ['ai-action', reviewScope, selected],
    async () => readback.read(selected!),
    { enabled: Boolean(selected), retry: false, refetchInterval: (data) =>
      data && ['succeeded', 'needs_review', 'cancelled', 'expired'].includes(data.status) ? false : 5000, cacheTime: 0 }
  );
  const action = detail.isError
    ? undefined
    : detail.data || history.data?.find((a) => a.id === selected);
  const reconciling = decision?.id === selected ? decision : null;
  useEffect(() => {
    if (
      !reconciling ||
      reconciling.phase === 'sending' ||
      !detail.data ||
      detail.isError
    )
      return;
    const status = detail.data.status;
    const settled = reconciling.approve
      ? status !== 'pending_review'
      : !['pending_review', 'approved'].includes(status);
    if (settled) setDecision(null);
  }, [detail.data, detail.dataUpdatedAt, detail.isError, reconciling]);
  const pending =
    history.data?.filter((a) => a.status === 'pending_review').length || 0;
  const refresh = async () => {
    await client.invalidateQueries(['ai-access', reviewScope]);
    await client.invalidateQueries(['ai-actions', reviewScope]);
    await client.invalidateQueries(['ai-action', reviewScope]);
  };
  const decide = async (id: string, approve: boolean) => {
    if (!readback.canRead()) return;
    setBusy(true);
    setDecisionError('');
    setDecision({ id, approve, phase: 'sending' });
    try {
      const response = await apiClient.post(
        `/ai-access/actions/${id}/${approve ? 'approve' : 'cancel'}`,
        undefined,
        { timeout: 30000 }
      );
      if (response.data?.id !== id || response.data?.company_id !== action?.company_id)
        throw new Error('Decision receipt did not match the reviewed action');
      client.setQueryData(['ai-action', reviewScope, id], response.data);
      setDecision(null);
    } catch (error: any) {
      readback.observe(error);
      const status = error?.response?.status;
      if (status >= 400 && status < 500 && ![408, 429].includes(status)) {
        setDecision(null);
        setDecisionError(
          error?.response?.data?.message || 'Decision was rejected.'
        );
      } else {
        setDecision({ id, approve, phase: 'checking' });
      }
    } finally {
      // Reconcile the original ID even if the decision response was lost.
      await client.invalidateQueries(['ai-action', reviewScope, id]);
      setDecision((current) =>
        current?.id === id ? { ...current, phase: 'unknown' } : current
      );
      setBusy(false);
      void client.invalidateQueries(['ai-actions', reviewScope]);
    }
  };
  const perform = async (work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      await refresh();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          'Operation failed. Refresh and check its status.'
      );
    } finally {
      setBusy(false);
    }
  };
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };
  const create = () =>
    perform(async () => {
      const result = await apiClient.post('/ai-access/credentials', {
        name,
        company_ids: tenants,
        tools,
        expires_in_days: days,
      });
      setToken(result.data.token);
      setCreating(false);
      setName('');
      setTenants([]);
      setTools([]);
    });
  const button =
    'inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-outline px-3 py-2 text-sm font-medium disabled:opacity-40';

  return (
    <PageLayout>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">AI Access</h1>
        <div className="flex gap-2">
          <button
            className={button}
            title="Refresh"
            aria-label="Refresh"
            onClick={() => void refresh()}
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
          <button
            className={`${button} bg-primary text-on-primary`}
            disabled={!config.data}
            onClick={() => setCreating(true)}
          >
            <PlusIcon className="h-5 w-5" />
            New connection
          </button>
        </div>
      </div>
      {config.isLoading && <p role="status">Loading connections...</p>}
      {config.isError && (
        <p role="alert" className="text-red-600">
          {(config.error as any)?.response?.status === 404
            ? 'AI Access is not enabled in this environment.'
            : 'Unable to load AI Access. Check your administrator access and try again.'}
        </p>
      )}
      {config.data && (
        <>
          <div className="mb-5 flex min-w-0 items-center gap-3 border-b border-outline/40 pb-5">
            <span className="text-sm font-medium">MCP endpoint</span>
            <code className="min-w-0 flex-1 break-all text-sm">
              {config.data.endpoint}
            </code>
            <button
              className={button}
              title="Copy endpoint"
              aria-label="Copy endpoint"
              onClick={() => void copy(config.data!.endpoint)}
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>
          </div>
          <div
            role="tablist"
            aria-label="AI access views"
            className="mb-5 flex gap-5 border-b border-outline/40"
          >
            {(['connections', 'actions'] as const).map((t) => (
              <button
                role="tab"
                aria-selected={tab === t}
                className={`border-b-2 px-1 py-3 text-sm font-semibold ${
                  tab === t
                    ? 'border-primary text-primary'
                    : 'border-transparent'
                }`}
                key={t}
                onClick={() => setTab(t)}
              >
                {t === 'actions'
                  ? `Activity${pending ? ` (${pending} to review)` : ''}`
                  : 'Connections'}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto">
            {tab === 'connections' ? (
              <table className="w-full min-w-[650px] text-left text-sm">
                <thead>
                  <tr className="border-b border-outline/50">
                    {[
                      'Connection',
                      'Tenants',
                      'Expires',
                      'Last used',
                      'Status',
                      '',
                    ].map((h, i) => (
                      <th key={i} className="px-3 py-3">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {config.data.credentials.map((c) => (
                    <tr className="border-b border-outline/30" key={c.id}>
                      <td className="px-3 py-4">
                        <div className="font-semibold">{c.name}</div>
                        <code className="text-xs">{c.prefix}...</code>
                      </td>
                      <td className="max-w-xs px-3 py-4">
                        {c.company_ids
                          .map(
                            (id) =>
                              config.data!.companies.find((t) => t.id === id)
                                ?.name || 'Unavailable tenant'
                          )
                          .join(', ')}
                      </td>
                      <td className="px-3 py-4">{date(c.expires_at)}</td>
                      <td className="px-3 py-4">{date(c.last_used_at)}</td>
                      <td className="px-3 py-4">
                        {c.revoked_at
                          ? 'Revoked'
                          : new Date(c.expires_at) < new Date()
                          ? 'Expired'
                          : 'Active'}
                      </td>
                      <td className="px-3 py-4">
                        <button
                          disabled={Boolean(c.revoked_at)}
                          className={button}
                          title={`Revoke ${c.name}`}
                          aria-label={`Revoke ${c.name}`}
                          onClick={() => setRevoke(c)}
                        >
                          <NoSymbolIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!config.data.credentials.length && (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-10 text-center text-on-surface-variant"
                      >
                        No connections
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              user && (
                <ActionActivity
                  key={reviewScope}
                  reviewScope={reviewScope}
                  readback={readback}
                  ownerId={operationsScope ? `${user.id}:${user.operations_grant_version || 0}:${operationsCompanyId}` : user.id}
                  actions={history.data || []}
                  onReview={(id) => {
                    setSelected(id);
                    setAcknowledged(false);
                    setDecisionError('');
                  }}
                />
              )
            )}
          </div>
          {history.isError && (
            <p role="alert" className="mt-3 text-red-600">
              Activity could not refresh. Do not repeat an operation until its
              status is confirmed.
            </p>
          )}
        </>
      )}
      {creating && config.data && (
        <AdminModalShell
          title="New AI connection"
          onClose={() => !busy && setCreating(false)}
          footer={
            <button
              disabled={
                busy || !name.trim() || !tenants.length || !tools.length
              }
              className={`${button} bg-primary text-on-primary`}
              onClick={create}
            >
              Create connection
            </button>
          }
        >
          <div className="space-y-5">
            <label className="block text-sm font-medium">
              Connection name
              <input
                className="mt-1 w-full rounded-md border border-outline bg-surface p-2"
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                placeholder="Brian - Claude"
              />
            </label>
            <label className="block text-sm font-medium">
              Expires in days
              <input
                type="number"
                min={1}
                max={90}
                className="ml-3 w-20 rounded-md border border-outline bg-surface p-2"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              />
            </label>
            <fieldset>
              <legend className="mb-2 font-semibold">Tenants</legend>
              <div className="max-h-48 space-y-2 overflow-y-auto">
                {config.data.companies.map((c) => (
                  <label className="flex items-center gap-2 text-sm" key={c.id}>
                    <input
                      type="checkbox"
                      checked={tenants.includes(c.id)}
                      onChange={() => setTenants(toggle(tenants, c.id))}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend className="mb-2 font-semibold">Operations</legend>
              <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
                {config.data.tools
                  .filter(
                    (t) => !['tenants_list', 'actions_get'].includes(t.name)
                  )
                  .map((t) => (
                    <label
                      className="flex items-start gap-2 text-sm"
                      title={t.description}
                      key={t.name}
                    >
                      <input
                        className="mt-1"
                        type="checkbox"
                        checked={tools.includes(t.name)}
                        onChange={() => setTools(toggle(tools, t.name))}
                      />
                      <span>
                        {label(t.name)}
                        <span className="block text-xs text-on-surface-variant">
                          {t.write ? 'Requires review' : 'Read only'}
                        </span>
                      </span>
                    </label>
                  ))}
              </div>
            </fieldset>
          </div>
        </AdminModalShell>
      )}
      {token && (
        <AdminModalShell
          title="Connection credential"
          onClose={() => setToken('')}
          footer={
            <button className={button} onClick={() => setToken('')}>
              Done
            </button>
          }
        >
          <p className="mb-4 text-sm text-amber-700">
            Shown once. Keep this credential private. Never paste it into a
            conversation or share it with another person.
          </p>
          <div className="flex min-w-0 items-start gap-2">
            <code className="min-w-0 flex-1 break-all rounded-md bg-surface-variant p-3 text-sm">
              {token}
            </code>
            <button
              className={button}
              title="Copy credential"
              aria-label="Copy credential"
              onClick={() => void copy(token)}
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>
          </div>
        </AdminModalShell>
      )}
      {revoke && (
        <AdminModalShell
          title={`Revoke ${revoke.name}?`}
          onClose={() => !busy && setRevoke(null)}
          footer={
            <button
              disabled={busy}
              className={`${button} text-red-600`}
              onClick={() =>
                void perform(async () => {
                  await apiClient.post(
                    `/ai-access/credentials/${revoke.id}/revoke`
                  );
                  setRevoke(null);
                })
              }
            >
              Revoke connection
            </button>
          }
        >
          <p>
            Pending actions will be cancelled. Work already started may still
            complete.
          </p>
        </AdminModalShell>
      )}
      {selected && !action && (
        <AdminModalShell
          title="Action outcome"
          onClose={() => !busy && setSelected(null)}
        >
          <p role={detail.isError ? 'alert' : 'status'}>
            {detail.isError
              ? 'Outcome unavailable. Access may have changed or the connection was interrupted. No decision or business failure is confirmed.'
              : 'Checking outcome...'}
          </p>
          <p className="mt-3 break-all text-sm">Action ID: {selected}</p>
          <button className={button} onClick={() => void detail.refetch()}>
            <ArrowPathIcon className="h-5 w-5" /> Check outcome
          </button>
        </AdminModalShell>
      )}
      {action && (
        <AdminModalShell
          title={label(action.tool)}
          onClose={() => !busy && setSelected(null)}
          maxWidthClassName="max-w-4xl"
          footer={
            <div className="flex flex-wrap items-center justify-end gap-3">
              {reconciling && (
                <button
                  className={button}
                  disabled={busy || detail.isFetching || detail.isError}
                  onClick={() => void decide(action.id, reconciling.approve)}
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  Retry {reconciling.approve ? 'approval' : 'cancellation'}
                </button>
              )}
              {['pending_review', 'approved'].includes(action.status) && (
                <button
                  className={button}
                  disabled={busy || Boolean(reconciling) || detail.isFetching}
                  onClick={() => void decide(action.id, false)}
                >
                  Cancel action
                </button>
              )}
              {action.status === 'pending_review' && (
                <button
                  className={`${button} bg-primary text-on-primary`}
                  disabled={
                    busy ||
                    Boolean(reconciling) ||
                    detail.isFetching ||
                    !acknowledged ||
                    Boolean(action.preview?.batch?.blocked) ||
                    new Date(action.expires_at) <= new Date()
                  }
                  onClick={() => void decide(action.id, true)}
                >
                  <CheckIcon className="h-5 w-5" />
                  Approve and run
                </button>
              )}
            </div>
          }
        >
          <p className="mb-3 break-all text-xs text-on-surface-variant">
            Action ID: {action.id}
          </p>
          {reconciling && (
            <p role="status" className="mb-4 text-sm text-amber-700">
              {reconciling.phase === 'sending'
                ? 'Submitting decision...'
                : reconciling.phase === 'checking'
                ? 'Checking outcome...'
                : 'Decision outcome not yet confirmed. Checking this action every five seconds.'}
            </p>
          )}
          {decisionError && (
            <p role="alert" className="mb-4 text-sm text-red-600">
              {decisionError}
            </p>
          )}
          <dl className="mb-5 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-on-surface-variant">Tenant</dt>
              <dd className="font-semibold">{action.preview?.tenant?.name}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">Status</dt>
              <dd>{actionStatus(action)}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">Review expires</dt>
              <dd>{date(action.expires_at)}</dd>
            </div>
            <div>
              <dt className="text-on-surface-variant">Customer email</dt>
              <dd>
                {action.preview?.customer_email_will_be_sent
                  ? 'Will be sent'
                  : 'Will not be sent'}
              </dd>
            </div>
          </dl>
          {action.preview?.warning && (
            <p className="mb-5 text-sm text-amber-700">
              {action.preview.warning}
            </p>
          )}
          <h3 className="mb-3 font-semibold">Requested changes</h3>
          {action.tool.startsWith('invoices_link_') ? (
            <InvoiceLinkReview
              key={action.id}
              rows={action.preview?.current?.rows || []}
              result={action.result}
            />
          ) : action.preview?.batch ? (
            <IntakeReview
              key={action.id}
              batch={action.preview.batch}
              result={action.result}
            />
          ) : (
            <RecordView value={action.preview?.input} />
          )}
          {action.preview?.current &&
            !action.tool.startsWith('invoices_link_') && (
              <details className="mt-5 border-t border-outline/40 pt-4">
                <summary className="cursor-pointer font-semibold">
                  Current records and terms
                </summary>
                <div className="mt-3">
                  <RecordView value={action.preview.current} />
                </div>
              </details>
            )}
          {action.result &&
            !action.preview?.batch &&
            !action.tool.startsWith('invoices_link_') && (
              <section className="mt-5 border-t border-outline/40 pt-4">
                <h3 className="mb-3 font-semibold">Result</h3>
                <RecordView value={action.result} />
              </section>
            )}
          {action.error && (
            <p role="alert" className="mt-4 text-sm text-red-600">
              {action.error}
            </p>
          )}
          {action.status === 'pending_review' && (
            <label className="mt-6 flex items-start gap-2 border-t border-outline/40 pt-4 text-sm">
              <input
                className="mt-1"
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              I approve these changes for {action.preview?.tenant?.name}
              {action.preview?.customer_email_will_be_sent
                ? ', including the customer email'
                : ''}
              .
            </label>
          )}
        </AdminModalShell>
      )}
    </PageLayout>
  );
}
